import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Label, InputField, Button, DeleteButton } from '../styling/StyledComponents';
import Navbar from '../Components/Navigation/Navbar';
import Footer from '../Components/Footer/Footer';
import PopupMessage from '../Components/PopupMessage'; // Import the new component
import Cookies from 'js-cookie';

function ProjectManagement() {
    const [projects, setProjects] = useState([]);
    const [newProjectName, setNewProjectName] = useState('');
    
    // State for Edit Project form
    const [selectedProject, setSelectedProject] = useState({
        id: -1,
        name: ''
    });

    // State for popup messages
    const [deletePopup, setDeletePopup] = useState({
        show: false,
        projectId: null,
        projectName: ''
    });

    const [successPopup, setSuccessPopup] = useState({
        show: false,
        message: ''
    });

    useEffect(() => {
        fetchProjects();
    }, []);

    const fetchProjects = async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/projects`, {
                headers: {
                    Authorization: `Bearer ${Cookies.get('token')}`
                },
                withCredentials: true
            });
            if (res.data.success) {
                setProjects(res.data.result);
            }
        } catch (error) {
            console.error('Failed to fetch projects:', error);
        }
    };

    const handleAddProject = async () => {
        if (!newProjectName) return;
        try {
            const res = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/project`,
                { name: newProjectName },
                {
                    headers: { Authorization: `Bearer ${Cookies.get('token')}` },
                    withCredentials: true
                }
            );
            if (res.data.success) {
                setNewProjectName('');
                fetchProjects();
                // Show success popup
                setSuccessPopup({
                    show: true,
                    message: 'Project added successfully!'
                });
            } else {
                // Show error popup
                setSuccessPopup({
                    show: true,
                    message: 'Failed to add project.'
                });
            }
        } catch (error) {
            console.error('Error adding project:', error);
            setSuccessPopup({
                show: true,
                message: 'Error adding project. Please try again.'
            });
        }
    };

    const handleUpdateProject = async () => {
        try {
            const res = await axios.put(
                `${process.env.REACT_APP_BACKEND_URL}/project/${selectedProject.id}`,
                { name: selectedProject.name },
                {
                    headers: { Authorization: `Bearer ${Cookies.get('token')}` },
                    withCredentials: true
                }
            );
            if (res.data.success) {
                // Reset the selected project after successful update
                setSelectedProject({
                    id: -1,
                    name: ''
                });
                fetchProjects();
                // Show success popup
                setSuccessPopup({
                    show: true,
                    message: 'Project updated successfully!'
                });
            } else {
                setSuccessPopup({
                    show: true,
                    message: 'Failed to update project.'
                });
            }
        } catch (error) {
            console.error('Error updating project:', error);
            setSuccessPopup({
                show: true,
                message: 'Error updating project. Please try again.'
            });
        }
    };

    // Shows delete confirmation popup
    const showDeleteConfirmation = (project) => {
        setDeletePopup({
            show: true,
            projectId: project.id,
            projectName: project.name
        });
    };

    // Handles actual deletion after confirmation
    const confirmDeleteProject = async () => {
        try {
            await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/project/${deletePopup.projectId}`, {
                headers: { Authorization: `Bearer ${Cookies.get('token')}` },
                withCredentials: true
            });
            
            // Close the popup
            setDeletePopup({
                show: false,
                projectId: null,
                projectName: ''
            });
            
            // Refresh projects
            fetchProjects();
            
            // Show success message
            setSuccessPopup({
                show: true,
                message: 'Project deleted successfully.'
            });
        } catch (error) {
            console.error('Error deleting project:', error);
            setDeletePopup({ show: false, projectId: null, projectName: '' });
            
            setSuccessPopup({
                show: true,
                message: 'Error deleting project. Please try again.'
            });
        }
    };

    // Handler for edit input change
    const handleEditInputChange = (e) => {
        const { name, value } = e.target;
        setSelectedProject(prev => ({
            ...prev,
            [name]: value
        }));
    };

    return (
        <div className='fill-height flex-column between'>
            <Navbar />
            <Label className='padding-m center'>Project Management</Label>

            {/* Add Project Form */}
            <div className='flex-column create-view fill-width-edit'>
                <Label className='padding-m'>Add New Project</Label>
                <div className='flex-column'>
                    <div className='flex-column m-margin'>
                        <Label>Project Name</Label>
                        <InputField
                            type="text"
                            placeholder="New Project Name"
                            value={newProjectName}
                            onChange={(e) => setNewProjectName(e.target.value)}
                        />
                    </div>
                    <Button onClick={handleAddProject}>Add Project</Button>
                </div>
            </div>

            {/* List of All Projects */}
            <div className='center flex-column padding-m'>
                <Label className='padding-m'>All Projects</Label>
                <div className="flex-column fill-width-edit">
                    {projects.map((project) => (
                        <div key={project.id} className="flex-row m-margin between align-center question-row">
                            <div className="flex-column">
                                <Label>{project.name}</Label>
                            </div>
                            <div className="flex-row">
                                <Button 
                                    className="m-margin" 
                                    onClick={() => setSelectedProject(project)}
                                >
                                    Edit
                                </Button>
                                <DeleteButton 
                                    className="m-margin" 
                                    onClick={() => showDeleteConfirmation(project)}
                                >
                                    Delete
                                </DeleteButton>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Edit Project Form - only shown when a project is selected */}
            {selectedProject.id !== -1 && (
                <div className='flex-column create-view fill-width-edit'>
                    <Label className='padding-m'>Edit Project</Label>
                    <div className='flex-column'>
                        <div className='flex-column m-margin'>
                            <Label>Project Name</Label>
                            <InputField
                                type="text"
                                name="name"
                                value={selectedProject.name}
                                onChange={handleEditInputChange}
                            />
                        </div>
                        <Button onClick={handleUpdateProject} type="submit">Update Project</Button>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Popup */}
            <PopupMessage
                show={deletePopup.show}
                title="Confirm Deletion"
                message={`Are you sure you want to delete the project "${deletePopup.projectName}"? This action cannot be undone.`}
                confirmText="Delete"
                cancelText="Cancel"
                onConfirm={confirmDeleteProject}
                onCancel={() => setDeletePopup({ show: false, projectId: null, projectName: '' })}
                isDanger={true}
            />

            {/* Success/Error Popup */}
            <PopupMessage
                show={successPopup.show}
                title="Notification"
                message={successPopup.message}
                confirmText="OK"
                onConfirm={() => setSuccessPopup({ show: false, message: '' })}
                onCancel={() => setSuccessPopup({ show: false, message: '' })}
            />

            <div className='padding-m'></div>
            <Footer />
        </div>
    );
}

export default ProjectManagement;